// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { Connection } from "../../Commons/Connection.js";
import { Resources, CurrentLang, LOCALE_ES } from "../../Commons/properties/Resources.js";
import { MainWindowManager } from "../UtilsClasses/MainWindowManager.js";
import { ExternalScreenTemplate } from "../../templates.js";
import { DockSystem, DockSystemInstance } from "../DockSystem.js";
import { ApplicationPanelNew } from "../panels/ApplicationPanelNew.js";
import { PanelNames } from "../UtilsClasses/FactoryConstants.js";
import { DynProperty } from "../../Commons/DynProperty.js";
import { externalScreenHandler } from "../../Utils/AppHandlers.js";
import { MathUtils } from "../../Utils/MathUtils.js";
import { WorkSpaceManager } from "../WorkSpaceManager.js";

export let ExternalScreen = ApplicationPanelNew.extend({
    data: function ()
    {
        return {
            width: 1024,
            height: 600,
            showFooter: false,
            resizable: false,
            url: null,
            headerText: null,
            aliases: null,
            loading: true,
            showHeader: true,
            dockablePanel: false,
            showAttachHeaderButton: true,
            canLinkByAccount: false,
            style_addition_header_button: "js-button-attach",
            clientTab: "",
            originalUrl: ""
        };
    },
    partials: { bodyPartial: ExternalScreenTemplate }
});

ExternalScreen.prototype.getType = function () { return PanelNames.ExternalScreen };

ExternalScreen.prototype.oncomplete = function ()
{
    ApplicationPanelNew.prototype.oncomplete.apply(this);

    var iframe = this.find('iframe')
    iframe.onload = function () { this.set('loading', false) }.bind(this)

    Resources.onLocaleChange.Subscribe(this.localize, this);

    this.on("attachClick", this.AttachClick);

    this.center();
};

ExternalScreen.prototype.dispose = function ()
{
    Resources.onLocaleChange.UnSubscribe(this.localize, this)

    ApplicationPanelNew.prototype.dispose.call(this)
};

ExternalScreen.prototype.localize = function ()
{
    let aliases = this.get('aliases'),
        currentLang = CurrentLang

    if (aliases)
        for (let i = 0; i < aliases.length; i++)
            if (aliases[i].lang === currentLang)
                this.set('headerText', aliases[i].toolName)

    this.set('header', this.get('headerText') || Resources.getResource('External panel'));
    this.set('header_button_text', Resources.getResource('panel.external.Attach'));
};

ExternalScreen.prototype.AttachClick = function ()
{
    this.set({ dockablePanel: true, showHeader: false })
    DockSystemInstance.addPanelOnDock(
        this,
        DockSystem.DockPlaceConst.Right,
        false);
    WorkSpaceManager.currentWorkspace.addPanel(this)
}

ExternalScreen.prototype.CustomPreparation = function ()
{
    this.set({ dockablePanel: true, showHeader: false })
}

//#region ICaller

ExternalScreen.prototype.callBack = function (properties)
{
    ApplicationPanelNew.prototype.callBack.call(this, properties);

    let dp = DynProperty.getPropertyByName(properties, 'headerText');
    if (dp && dp.value) this.set('headerText', dp.value);

    dp = DynProperty.getPropertyByName(properties, 'clientTab');
    if (dp && dp.value) this.set('clientTab', dp.value);

    dp = DynProperty.getPropertyByName(properties, 'originalUrl');
    if (dp && dp.value) this.set('originalUrl', dp.value);

    this.UpdateAfterLoad();
};

ExternalScreen.prototype.Properties = function ()
{
    var properties = ApplicationPanelNew.prototype.Properties.call(this);

    var headerText = this.get('headerText');
    if (headerText) properties.push(new DynProperty("headerText", headerText, DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    var clientTab = this.get('clientTab');
    if (clientTab) properties.push(new DynProperty("clientTab", clientTab, DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    var originalUrl = this.get('originalUrl');
    if (originalUrl) properties.push(new DynProperty("originalUrl", originalUrl, DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    return properties;
};

//#endregion


ExternalScreen.prototype.UpdateAfterLoad = function ()
{
    var headerText = this.get('headerText');
    var clientTab = this.get('clientTab');
    var originalUrl = this.get('originalUrl');

    let tool = null;
    if (headerText && clientTab)
        tool = MainWindowManager.MainWindow.ExternalScreenStorage.GetTool(headerText, clientTab)

    if (tool)
    {
        this.UpdateLink(tool.url, tool.IsNoneLink(), tool)
        this.set({ originalUrl: tool.url });
        MainWindowManager.MainWindow.ExternalScreenStorage.AddScreenItem(tool, this);
    }
    else if (originalUrl)
        this.UpdateLink(originalUrl)
}


ExternalScreen.prototype.UpdateLink = function (newUrl, cleanUrl, tool)
{
    let culture = CurrentLang === LOCALE_ES ? 'es-ES' : 'en-GB',
        url = newUrl,
        token = Connection.vendor.loginParams.accessToken

    if (this.getRefreshURL)
        url = this.getRefreshURL()

    if (!cleanUrl)
        url += '?token=' + token + '&theme=night&language=' + culture + '&platform=TraderEvolution'

    this.set({ url: url });

    if (tool)
    {
        let needLocalize = false,
            dataToUpdate = {
                clientTab: tool.clientTab,
                id: tool.id
            }

        if (tool.name)
        {
            dataToUpdate.headerText = tool.name
            needLocalize = true
        }

        if (tool.aliases)
        {
            dataToUpdate.aliases = tool.aliases
            needLocalize = true
        }

        this.set(dataToUpdate);

        if (needLocalize)
            this.localize()
    }
}

ExternalScreen.Show = function (messageInfo, cleanUrl)
{
    let culture = CurrentLang === LOCALE_ES ? 'es-ES' : 'en-GB',
        url = messageInfo.Text,
        getRefreshURLFunc = messageInfo.GetRefreshURL,
        tool = messageInfo.Tool,
        screenHeader = messageInfo.ScreenName,
        aliases = messageInfo.Aliases,
        clientTab = messageInfo.ClientTab,
        token = Connection.vendor.loginParams.accessToken //  App.Connection.vendor.userSessionId //  #88483

    if (!cleanUrl)
        url += '?token=' + token + '&theme=night&language=' + culture + '&platform=TraderEvolution'

    if (!messageInfo.ClientUseInternalBrowser)
    {
        window.open(url)
        return null;
    }

    var scr = new ExternalScreen();
    scr.set({
        url: url,
        headerText: screenHeader,
        aliases: aliases,
        clientTab: clientTab,
        originalUrl: messageInfo.Text
    });

    if (messageInfo.ScreenWidth)
        scr.set('width', messageInfo.ScreenWidth)
    if (messageInfo.ScreenHeight)
        scr.set('height', messageInfo.ScreenHeight)

    if (!MathUtils.IsNullOrUndefined(messageInfo.ResizableScreen))
        scr.set('resizable', messageInfo.ResizableScreen)

    if (getRefreshURLFunc)
        scr.getRefreshURL = getRefreshURLFunc

    MainWindowManager.MainWindow.addControl(scr);
    scr.setFocus();
    scr.localize();

    MainWindowManager.MainWindow.ExternalScreenStorage.AddScreenItem(tool, scr);

    return scr; 
}

let externalScrHanl = externalScreenHandler;
externalScrHanl.Show = ExternalScreen.Show;
